









































































































































































































































































































































































































































































































































































































import Vue from "vue";
import Modal from "@/components/root/modal/Modal.vue";
import ModalTabs, { TabItem } from "@/components/root/modal/ModalTabs.vue";
import { ModalConfig } from "@/components/root/modal";
import { ref, computed, onMounted } from "@vue/composition-api";
import { SharedState } from "@/common/state";
import { POPULATION_TYPE } from "@/common/models";
import { AppAnalytics } from "@/analytics";
import { uniq_by_keys } from "@/fp";
import EmptyCell from "../EmptyCell.vue";
import { sortBy } from "@/fp";
import SpiMarker from "../SpiMarker.vue";
import { FileModelHelper } from "@/gql/file-parser";
import { CopyUrl } from "@/common/clipboard";
import { getInsightDisplayName } from "./event-details.helpers";
import { RiskStoryEntity } from "./risk-story/risk-story.types";
import { formatRiskStory } from "./risk-story/risk-story.narrative";

const SEVERITY_LABELS: Record<number, string> = {
  0: "None",
  1: "Low",
  2: "Medium",
  3: "High",
};

const SENSITIVITY_LABELS: Record<number, string> = {
  1: "Public",
  2: "Internal",
  3: "Confidential",
  4: "Restricted",
};

export default Vue.extend({
  name: "RiskStoryDetailsModal",
  components: {
    Modal,
    ModalTabs,
    EmptyCell,
    SpiMarker,
  },
  props: ["modalData"],
  setup(props: any, { emit, root: { $router } }: any) {
    const feature_pii = SharedState.FeatureHelper.is_enabled("pii_labeler");
    const config: ModalConfig = {
      title: "",
      desc: "",
      blank: true,
      width: feature_pii ? "82rem" : "60rem",
      height: "50rem",
      withTopRightCloseBtn: true,
    };

    const PII_LABELS_CATEGORY_TO_HIDE = [
      "MIP Labels",
      "Virtual",
      "Has MIP Label",
      "External Information",
      "Internal Information",
    ];

    const featureTopics = computed(
      () => SharedState.loggedUser.value.paymentEdition.topics
    );

    // ── Summary strip computed helpers ──────────────────────────────────
    // The risk story is the source of truth for risk content; the event object
    // (source/date) is only supplementary and may be absent (e.g. opened by id).
    const riskStorySummary = computed<any>(
      () => (props.modalData as any)?.riskStorySummary || null
    );
    const severity = computed<number>(() => {
      const raw = (props.modalData as any).item.severity;
      const numeric = Number(raw);
      if (Number.isFinite(numeric) && numeric > 0) return numeric;
      const s = riskStorySummary.value;
      return s && Number.isFinite(Number(s.severity)) ? Number(s.severity) : 0;
    });
    const eventDate = computed(
      () =>
        (props.modalData as any)?.item?.date ||
        (riskStorySummary.value && riskStorySummary.value.date) ||
        ""
    );
    const hasEventSource = computed(
      () => !!(props.modalData as any)?.item?.source
    );
    const hasRisk = computed(() => severity.value > 0);
    const severityLabel = computed(() => SEVERITY_LABELS[severity.value] || "None");
    const chipClassBySeverity = (level: number) => {
      switch (level) {
        case 3: return "severity-chip--high";
        case 2: return "severity-chip--medium";
        case 1: return "severity-chip--low";
        default: return "severity-chip--none";
      }
    };
    const severityChipClass = computed(() => chipClassBySeverity(severity.value));

    // Insight from the event whose severity matches the event severity, falling back to the first.
    const riskInsight = computed(() => {
      const rawInsights: any[] = (props.modalData as any).item.insights || [];
      if (!rawInsights.length) return null;
      const resolve = (raw: any) => {
        const indexed = (SharedState as any).indexed_insights?.value?.[raw.id];
        if (!indexed) return raw;
        // Prefer the event-embedded description when the global store has none
        return indexed.description ? indexed : { ...indexed, description: raw.description || "" };
      };
      // 1. Prefer an insight whose current severity (insight manager) matches the event severity
      const currentMatch = rawInsights.find((raw) => {
        const ins = resolve(raw);
        return Number(ins.severityLevel ?? ins.severity) === severity.value;
      });
      if (currentMatch) return resolve(currentMatch);
      // 2. Fall back to the insight that had the matching severity at event-creation time (raw data)
      const historicalMatch = rawInsights.find((raw) => Number(raw.severity) === severity.value);
      if (historicalMatch) return resolve(historicalMatch);
      // 3. Final fallback: first insight
      return resolve(rawInsights[0]);
    });

    const riskName = computed(() => {
      const ins = riskInsight.value as any;
      return getInsightDisplayName(ins);
    });

    // Insight manager's current severity for this risk (may differ from event-recorded severity)
    const insightSeverityLevel = computed<number>(() => {
      const ins = riskInsight.value as any;
      if (!ins) return severity.value;
      const raw = ins.severityLevel ?? ins.severity;
      const numeric = Number(raw);
      return Number.isFinite(numeric) ? numeric : severity.value;
    });

    const severityLabelWithSuffix = (level: number) => {
      const base = SEVERITY_LABELS[level];
      return base && base !== "None" ? `${base} Severity` : "";
    };

    // When the current insight severity is None (0), fall back to the event's recorded severity so
    // the chip is never blank for an event that has a meaningful risk level.
    const displaySeverityLevel = computed(() =>
      insightSeverityLevel.value > 0 ? insightSeverityLevel.value : severity.value
    );
    const insightSeverityLabel = computed(() => severityLabelWithSuffix(displaySeverityLevel.value));
    const insightSeverityChipClass = computed(() => chipClassBySeverity(displaySeverityLevel.value));

    // Show "Previously" badge only when both severities are non-zero and differ
    const hasPreviousSeverity = computed(
      () => hasRisk.value && insightSeverityLevel.value > 0 && insightSeverityLevel.value !== severity.value
    );
    const previousSeverityLabel = computed(() => severityLabelWithSuffix(severity.value));
    const previousSeverityChipClass = computed(() => chipClassBySeverity(severity.value));

    // Sensitivity level derived from the first item
    const sensitivityLevel = computed<number>(() => {
      const items = (props.modalData as any).item.items || [];
      const firstItem = items[0];
      const sensRaw = firstItem?.sensitivity ?? firstItem?.labels?.infoLabel?.sensitivity;
      const sens = Number(sensRaw);
      if (sens && SENSITIVITY_LABELS[sens]) return sens;
      if (severity.value >= 3) return 3;
      if (severity.value >= 2) return 2;
      return 0;
    });

    const sensitivityChipClass = computed(() => {
      switch (sensitivityLevel.value) {
        case 4:
        case 3: return "sensitivity-chip--high";
        case 2: return "sensitivity-chip--medium";
        case 1: return "sensitivity-chip--low";
        default: return "";
      }
    });

    // Display label for sensitivity using severity-like scale ("High Sensitivity" etc.)
    const SENSITIVITY_DISPLAY_LABELS: Record<number, string> = { 1: "Low", 2: "Medium", 3: "High", 4: "High" };
    const fullSensitivityLabel = computed(() => {
      const label = SENSITIVITY_DISPLAY_LABELS[sensitivityLevel.value];
      return label ? `${label} Sensitivity` : "";
    });

    // Derive sensitivity label from the first item's sensitivity value
    const sensitivityLabel = computed(() => {
      const items = (props.modalData as any).item.items || [];
      const firstItem = items[0];
      const sensRaw = firstItem?.sensitivity ?? firstItem?.labels?.infoLabel?.sensitivity;
      const sens = Number(sensRaw);
      if (sens && SENSITIVITY_LABELS[sens]) return SENSITIVITY_LABELS[sens];
      // Fallback: if severity is high, show "Confidential"
      if (severity.value >= 3) return "Confidential";
      if (severity.value >= 2) return "Internal";
      return "";
    });

    // Derive content type name from the first item's content label
    const contentTypeName = computed(() => {
      const items = (props.modalData as any).item.items || [];
      const firstItem = items[0];
      const name = firstItem?.labels?.infoLabel?.name;
      return name || "";
    });

    // Icon for event type row
    const eventTypeIcon = computed(() => {
      const eventType = (props.modalData as any).item.event_type || "";
      const lower = eventType.toLowerCase();
      if (lower.includes("email")) return "Email";
      if (lower.includes("upload")) return "Upload";
      if (lower.includes("download")) return "Download";
      if (lower.includes("copy") || lower.includes("copied")) return "Copy";
      if (lower.includes("sharing") || lower.includes("shared")) return "Share";
      if (lower.includes("delete")) return "Delete";
      return "File";
    });

    const riskStoryText = computed(() => {
      // Prefer the per-file narratives array (already one sentence per file) so
      // the reformatter groups from clean input; fall back to the joined `story`
      // string. formatRiskStory regroups by recipient, or returns the source
      // text unchanged if it doesn't match the expected template (ADO-24865).
      const s = riskStorySummary.value;
      if (s && Array.isArray(s.narratives) && s.narratives.length) {
        return formatRiskStory(s.narratives);
      }
      const raw = (props.modalData as any)?.item?.story || "";
      if (typeof raw === "string" && raw.trim()) {
        return formatRiskStory(raw.trim());
      }
      return "";
    });
    const hasRiskStory = computed(() => !!riskStoryText.value);

    const triageDecision = computed(() => {
      const raw = (props.modalData as any)?.item?.triage?.decision;
      return typeof raw === "string" ? raw.trim() : "";
    });
    const hasRiskTriage = computed(() =>
      hasRisk.value && !!triageDecision.value
    );
    const triageDecisionClass = computed(() => {
      const d = triageDecision.value.toLowerCase();
      if (!d) return "triage-decision-chip--default";
      if (d.includes("dismiss") || d.includes("no risk") || d.includes("resolved")) {
        return "triage-decision-chip--success";
      }
      if (d.includes("escalat") || d.includes("critical") || d.includes("high")) {
        return "triage-decision-chip--danger";
      }
      if (d.includes("review") || d.includes("pending")) {
        return "triage-decision-chip--warning";
      }
      return "triage-decision-chip--default";
    });

    type TrustRecipient = {
      entity: any;
      trusted: boolean;
      key: string;
    };

    const trustInfoLoaded = ref(false);
    const trustInfoAvailable = ref(false);
    const trustEntriesMap = ref<Record<string, boolean>>({});
    const trustLoading = ref(false);

    const get_entity_domain = (entity: any): string => {
      const email = entity?.email || "";
      if (typeof email !== "string" || !email.includes("@")) {
        return "";
      }
      return email.split("@").pop() || "";
    };

    const get_entity_type_label = (entity: any): string => {
      if (!entity) return "Entity";
      if (entity.type == "organization") return "Organization";
      if (entity.isExternal) return "External contact";
      return "Employee";
    };

    const build_entity_keys = (entity: any): string[] => {
      const candidates = [
        entity?.id,
        entity?.email,
        entity?.name,
        get_entity_domain(entity),
      ];
      return candidates
        .filter((v) => typeof v === "string" && v.trim())
        .map((v) => (v as string).trim().toLowerCase());
    };

    const exposedByPrimary = computed(
      () => (props.modalData as any)?.item?.exposedBy || null
    );
    // The exposed-to entities come from the Risk Story API context (the at-risk
    // recipients), each shaped for the existing entity-card template. Trust is the
    // entity's enriched currentTrust (from effective-trust-familiarity-aggregations).
    const exposedToList = computed(() =>
      riskStoryEntities.value.map((e) => ({
        id: e.entityId,
        name: e.entityName,
        email: e.entityName,
        type: e.entityType,
        isExternal: false,
        currentTrust: e.currentTrust,
      }))
    );
    const hasExposureEntities = computed(
      () => !!exposedByPrimary.value || exposedToList.value.length > 0
    );

    const get_entity_trust = (entity: any): boolean | null => {
      // Risk-story entities carry their enriched current trust directly.
      if (entity && entity.currentTrust) {
        return entity.currentTrust === "trusted";
      }
      if (!trustInfoAvailable.value) {
        return null;
      }
      const keys = build_entity_keys(entity);
      for (const key of keys) {
        if (key in trustEntriesMap.value) {
          return trustEntriesMap.value[key];
        }
      }
      return null;
    };

    const exposedByTrust = computed(() => {
      const trust = get_entity_trust(exposedByPrimary.value);
      if (trust === null) return "unknown";
      return trust ? "trusted" : "untrusted";
    });
    const hasUntrustedExposedBy = computed(
      () =>
        trustInfoAvailable.value &&
        !!exposedByPrimary.value &&
        exposedByTrust.value === "untrusted"
    );
    const hasTrustedExposedBy = computed(
      () =>
        trustInfoAvailable.value &&
        !!exposedByPrimary.value &&
        exposedByTrust.value !== "untrusted"
    );

    const orderedRecipients = computed<TrustRecipient[]>(() => {
      const entities = exposedToList.value;
      if (!trustInfoAvailable.value) {
        return entities.map((entity: any, idx: number) => ({
          entity,
          trusted: true,
          key: `recipient-${idx}-${entity?.id || entity?.email || entity?.name || "unknown"}`,
        }));
      }
      const mapped = entities.map((entity: any, idx: number) => {
        const trust = get_entity_trust(entity);
        return {
          entity,
          trusted: trust !== false,
          key: `recipient-${idx}-${entity?.id || entity?.email || entity?.name || "unknown"}`,
        };
      });
      const untrusted = mapped.filter((m) => !m.trusted);
      const trusted = mapped.filter((m) => m.trusted);
      return [...untrusted, ...trusted];
    });

    const untrustedRecipients = computed(() =>
      trustInfoAvailable.value ? orderedRecipients.value.filter((r) => !r.trusted) : []
    );
    const trustedRecipients = computed(() =>
      trustInfoAvailable.value ? orderedRecipients.value.filter((r) => r.trusted) : []
    );
    const untrustedEntitiesCount = computed(
      () => untrustedRecipients.value.length + (hasUntrustedExposedBy.value ? 1 : 0)
    );
    const hasUntrustedEntities = computed(() => untrustedEntitiesCount.value > 0);
    const hasTrustedEntities = computed(
      () => hasTrustedExposedBy.value || trustedRecipients.value.length > 0
    );
    const trustChangedEntities = computed(() =>
      riskStoryEntities.value.filter((e) => e.hasTrustChange)
    );
    const hasTrustChanges = computed(() => trustChangedEntities.value.length > 0);
    const trustPillClass = (level?: string) => {
      if (level === "trusted") return "trust-pill--trusted";
      if (level === "untrusted") return "trust-pill--untrusted";
      return "trust-pill--unmanaged";
    };
    const TRUSTED_COLLAPSE_THRESHOLD = 12;
    const TRUSTED_PREVIEW_COUNT = 8;
    const showAllTrusted = ref(false);
    const visibleTrustedRecipients = computed(() =>
      trustedRecipients.value.length <= TRUSTED_COLLAPSE_THRESHOLD
        ? trustedRecipients.value
        : showAllTrusted.value
        ? trustedRecipients.value
        : trustedRecipients.value.slice(0, TRUSTED_PREVIEW_COUNT)
    );
    const hasMoreTrustedRecipients = computed(
      () => trustedRecipients.value.length > TRUSTED_COLLAPSE_THRESHOLD
    );
    const hiddenTrustedCount = computed(
      () =>
        trustedRecipients.value.length > TRUSTED_COLLAPSE_THRESHOLD
          ? Math.max(0, trustedRecipients.value.length - TRUSTED_PREVIEW_COUNT)
          : 0
    );
    const toggleTrustedRecipients = () => {
      showAllTrusted.value = !showAllTrusted.value;
    };

    const riskStoryEntities = ref<RiskStoryEntity[]>(
      ((props.modalData as any)?.riskStoryEntities as RiskStoryEntity[]) || []
    );

    const fetch_event_trust = async () => {
      const entities = riskStoryEntities.value;
      // Driven by the risk story, not the event's severity: a risk story existing
      // means there are entities to show, regardless of the event-object payload.
      if (entities.length === 0) {
        trustInfoLoaded.value = true;
        trustInfoAvailable.value = false;
        return;
      }
      const mapped: Record<string, boolean> = {};
      entities.forEach((e) => {
        // current trust is the source of truth for the trusted/untrusted split
        const trusted = e.currentTrust === "trusted";
        [e.entityId, e.entityName]
          .filter((v) => typeof v === "string" && v.trim())
          .forEach((k) => {
            mapped[(k as string).trim().toLowerCase()] = trusted;
          });
      });
      trustEntriesMap.value = mapped;
      trustInfoAvailable.value = true;
      trustInfoLoaded.value = true;
      trustLoading.value = false;
    };

    // files
    const files = computed(() => {
      const eventItems = ((props.modalData as any).item.items || []) as any[];
      // Fall back to the risk story content (file name only) when the event
      // object has no items — the Risk Story API is the source of truth.
      if (eventItems.length === 0) {
        const s = riskStorySummary.value;
        const content = (s && Array.isArray(s.content) && s.content) || [];
        return content.map((c: any, idx: number) => ({
          id: c.contentId || `rs-content-${idx}`,
          name: c.contentName || "Unknown",
          is_email_body: false,
          clusterUID: c.contentId || "",
          archive: null,
          archive_items: [],
          content_label: { name: "", tree: [] },
          piis: [],
        }));
      }
      const archiveNameToFiles = eventItems.reduce(
        (acc, item) => {
          const archiveName = item?.archive?.archiveName;
          if (archiveName && item?.name) {
            if (!acc[archiveName]) {
              acc[archiveName] = [];
            }
            acc[archiveName].push(item.name);
          }
          return acc;
        },
        {} as Record<string, string[]>
      );

      return eventItems.map((file: any) => {
        console.log("file::", file);

        const info_label_id = file?.labels?.infoLabel?.id;
        const info_label = info_label_id
          ? SharedState.getInfoType(info_label_id)
          : undefined;
        // @ts-ignore
        const info_label_path = Array.isArray(info_label?.path)
        // @ts-ignore
          ? (info_label?.path as any[])
          : [];
        const info_label_tree = info_label_path
          .map((id: any) => SharedState.getInfoType(id))
          .filter(Boolean)
          // @ts-ignore
          .map((label) => label.labelName);

        const ctxLabels = Array.isArray(file?.labels?.ctxLabels)
          ? file.labels.ctxLabels
          : [];
        const piisLabels = Array.isArray(file?.labels?.piis)
          ? file.labels.piis
          : [];
        const archiveItemsRaw = Array.isArray(file?.internalItems)
          ? file.internalItems
              .filter((d: any) => d && d.name)
              .map((d: any) => d.name)
          : [];
        const fallbackArchiveItems =
          file?.archive?.archiveName &&
          archiveNameToFiles[file.archive.archiveName]
            ? [...archiveNameToFiles[file.archive.archiveName]]
          : [];
        const archiveItemsSource =
          archiveItemsRaw.length > 0
            ? archiveItemsRaw
            : fallbackArchiveItems;
        const archive_items = archiveItemsSource;

        return {
          ...file,
          is_email_body: FileModelHelper.is_email_body(file),
          context: ctxLabels.map((d: any) => d.name).join(", "),
          content_label: {
            ...file?.labels?.infoLabel,
            tree:
              info_label_tree.length > 0
                ? info_label_tree.slice(0, info_label_tree.length - 1)
                : [],
            labelName:
              info_label?.labelName || file?.labels?.infoLabel?.name || "",
          },
          piis: piisLabels.filter((d: { labelCategory: string }) =>
            PII_LABELS_CATEGORY_TO_HIDE.includes(d.labelCategory)
              ? false
              : true
          ),
          archive_items,
          topic: file?.extendedProperties ? file.extendedProperties.topic : "",
        };
      });
    });

    const event_insights = computed(() =>
      uniq_by_keys(
        "shareType",
        "sensitivity"
      )(
        (props.modalData as any).item.insights
          .filter((l: any) => {
            const ins = SharedState.indexed_insights.value[l.id];
            if (!ins) return false;
            return ins.infoTypeId;
          })
          .map((l: any) => SharedState.indexed_insights.value[l.id])
      )
    );

    const event_severity_insights = computed(() =>
      event_insights.value.filter(
        // @ts-ignore
        (ins) => ins.severityLevel === props.modalData.item.severity
      )
    );

    const other_event_insights = computed(() =>
      sortBy(
        event_insights.value.filter(
          // @ts-ignore
          (ins) => ins.severityLevel !== props.modalData.item.severity
        ),
        "severityLevel",
        true
      )
    );

    const tabs = computed(() => {
      // exposedTo is a legacy event-object field and is absent on the
      // risk-story-driven payload, so guard it. The Entities count now reflects
      // the risk-story entities (the source of truth), falling back to the old
      // exposedTo recipients + 1 (exposedBy) when those aren't present.
      const exposedTo =
        (props.modalData.item && (props.modalData.item as any).exposedTo) || [];
      const entitiesCount =
        riskStoryEntities.value.length || (exposedTo.length ? exposedTo.length + 1 : 0);
      return [
        {
          name: "Overview",
          icon: "Summary",
        },
        {
          name: "Content",
          icon: "File",
          count: files.value.length,
        },
        {
          name: "Entities",
          icon: "User",
          count: entitiesCount,
        },
      ] as TabItem[];
    });

    const normalizeSectionName = (section: string) => {
      switch (section) {
        case "Summary":
        case "Overview":
        case "Risks":
          return "Overview";
        case "Files":
        case "Items":
        case "Content":
          return "Content";
        case "People":
        case "Entities":
          return "Entities";
        default:
          return "Overview";
      }
    };

    const incomingSection = String((props.modalData as any).section || "");
    const selectedTabName = ref(normalizeSectionName(incomingSection));

    const invEntity = async (entity: any) => {
      AppAnalytics.report({
        component: "General",
        eventName: `user presed a person to investigate in EventRowDetailsModal in page :${$router.currentRoute.name}`,
      });

      let type;
      switch (entity.type) {
        case "organization":
          type = POPULATION_TYPE.ORGANIZATION;
          break;
        case "person":
        default:
          type = entity.isExternal
            ? POPULATION_TYPE.CONTACT
            : POPULATION_TYPE.EMPLOYEE;
          break;
      }

      emit("submit", {
        action: "investigate",
        payload: {
          ...entity,
          type,
        },
      });
    };

    const goToCluster = async (file: any) => {
      AppAnalytics.report({
        component: "General",
        eventName: `user presed a file to investigate in EventRowDetailsModal in page :${$router.currentRoute.name}`,
      });
      emit("submit", {
        action: "investigate",
        payload: {
          ...file,
          cluster_id: file.clusterUID,
          type: POPULATION_TYPE.FILE,
        },
      });
    };

    onMounted(() => {
      console.log("eventRowDetails:", props.modalData);
      AppAnalytics.report({
        component: "General",
        eventName: `user opened EventRowDetailsModal in page :${$router.currentRoute.name}`,
      });
      showAllTrusted.value = false;
      fetch_event_trust();
    });

    const decodeUriSafe = (value: string) => {
      try {
        return decodeURIComponent(value);
      } catch {
        return value;
      }
    };

    const appendFileNameToUrl = (url: string, fileName: string) => {
      if (!url) return "";
      if (!fileName) return url;

      const decodedUrl = decodeUriSafe(url);
      const decodedFileName = decodeUriSafe(fileName);
      if (
        decodedUrl.endsWith(`/${decodedFileName}`) ||
        decodedUrl.endsWith(decodedFileName)
      ) {
        return url;
      }

      const [baseAndQuery, hash = ""] = url.split("#");
      const [basePath, query = ""] = baseAndQuery.split("?");
      const normalizedBasePath = basePath.replace(/\/+$/, "");
      const normalizedFileName = fileName.replace(/^\/+/, "");
      const urlWithFileName = `${normalizedBasePath}/${normalizedFileName}`;

      return `${urlWithFileName}${query ? `?${query}` : ""}${
        hash ? `#${hash}` : ""
      }`;
    };

    // File URL for the Content tab header
    const fileUrl = computed(() => {
      const item = (props.modalData as any).item;
      const firstFile = item.items?.[0];
      const baseUrl = item.file_upload_path || firstFile?.path || "";
      const fileName = firstFile?.name || "";
      return appendFileNameToUrl(baseUrl, fileName);
    });

    const fileName = computed(() => {
      const item = (props.modalData as any).item;
      const firstFile = item.items?.[0];
      return firstFile?.name || "";
    });

    const fileUrlDisplay = computed(() => {
      try {
        return decodeURIComponent(fileUrl.value as unknown as string);
      } catch {
        return fileUrl.value;
      }
    });

    const truncateMiddle = (value: string, maxLength = 90) => {
      if (!value || value.length <= maxLength) return value;

      const separator = "...";
      const availableChars = maxLength - separator.length;
      const frontChars = Math.ceil(availableChars / 2);
      const backChars = Math.floor(availableChars / 2);

      return `${value.slice(0, frontChars)}${separator}${value.slice(
        value.length - backChars
      )}`;
    };

    const fileUrlTruncatedDisplay = computed(() =>
      truncateMiddle(fileUrlDisplay.value as string)
    );

    const copyFileUrl = () => {
      CopyUrl(fileUrl.value, 'URL copied to clipboard');
    };

    return {
      feature_pii,
      config,
      selectedTabName,
      tabs,
      files,
      event_insights,
      event_severity_insights,
      other_event_insights,
      featureTopics,
      goToCluster,
      invEntity,
      fileUrl,
      fileUrlDisplay,
      fileUrlTruncatedDisplay,
      fileName,
      copyFileUrl,
      // Summary strip
      hasRisk,
      eventDate,
      hasEventSource,
      severity,
      severityLabel,
      severityChipClass,
      riskName,
      insightSeverityLabel,
      insightSeverityChipClass,
      hasPreviousSeverity,
      previousSeverityLabel,
      previousSeverityChipClass,
      sensitivityLabel,
      sensitivityChipClass,
      fullSensitivityLabel,
      contentTypeName,
      eventTypeIcon,
      riskStoryText,
      hasRiskStory,
      triageDecision,
      hasRiskTriage,
      triageDecisionClass,
      trustInfoAvailable,
      riskStoryEntities,
      trustChangedEntities,
      trustPillClass,
      hasTrustChanges,
      trustInfoLoaded,
      trustLoading,
      exposedByPrimary,
      exposedByTrust,
      hasUntrustedExposedBy,
      hasTrustedExposedBy,
      hasExposureEntities,
      orderedRecipients,
      untrustedRecipients,
      trustedRecipients,
      untrustedEntitiesCount,
      hasUntrustedEntities,
      hasTrustedEntities,
      visibleTrustedRecipients,
      hasMoreTrustedRecipients,
      hiddenTrustedCount,
      showAllTrusted,
      toggleTrustedRecipients,
      get_entity_type_label,
      get_entity_domain,
    };
  },
});
